import React, { useMemo, useState } from 'react';
import { getMaxRotateForImage } from './decalEngine';

/**
 * User-editable image array for Rare decals (ciggies, fry_1, fry_2, rhino, vape).
 * Clean root-relative URLs pointing to local artwork assets.
 */
export const RARE_ITEM_IMAGES: (string | { src: string; maxRotate?: number })[] = [
  '/Images/Rare/ciggies.png',
  '/Images/Rare/fry_1.png',
  '/Images/Rare/fry_2.webp',
  '/Images/Rare/rhino.png',
  '/Images/Rare/vape.png'
];

export interface RareItemProps {
  x?: number; // percentage (0 - 100)
  y?: number; // percentage (0 - 100)
  rotation?: number; // degrees
  /** Custom +/- max rotation clamp (0 - 360). Defaults to 360 (full rotation) */
  maxRotate?: number;
  scale?: number;
  opacity?: number;
  imageSrc?: string;
  images?: (string | { src: string; maxRotate?: number })[];
  dropShadow?: string;
  size?: number | string;
  className?: string;
  style?: React.CSSProperties;
}

export const RareItem: React.FC<RareItemProps> = ({
  x = 50,
  y = 50,
  rotation = 0,
  maxRotate,
  scale = 1,
  opacity = 1,
  imageSrc,
  images,
  dropShadow,
  size = 130,
  className = '',
  style
}) => {
  const pool = images && images.length > 0 ? images : RARE_ITEM_IMAGES;
  const chosen = useMemo(() => {
    if (imageSrc) return { src: imageSrc, maxRotate };
    const idx = Math.floor(Math.random() * pool.length);
    const item = pool[idx];
    if (typeof item === 'string') {
      return { src: item, maxRotate };
    }
    return { src: item.src, maxRotate: item.maxRotate ?? maxRotate };
  }, [imageSrc, pool, maxRotate]);

  const [currentSrc, setCurrentSrc] = useState(chosen.src);

  // Dedicated drop shadow matching ComputerParts: drop-shadow(2px 3px 5px rgba(0, 0, 0, 0.45))
  const rareFilter = dropShadow || 'drop-shadow(2px 3px 5px rgba(0, 0, 0, 0.45))';

  // Rotation logic: defaults to 360 (full rotation), or custom maxRotate
  const effectiveMaxRotate = useMemo(() => {
    return getMaxRotateForImage(currentSrc, chosen.maxRotate ?? maxRotate, 'rare');
  }, [currentSrc, chosen.maxRotate, maxRotate]);

  const normalized = ((rotation % 360) + 540) % 360 - 180;
  const clampedRotation = effectiveMaxRotate < 360
    ? Math.max(-effectiveMaxRotate, Math.min(effectiveMaxRotate, normalized))
    : rotation;

  return (
    <div
      className={`random-thing decal-rare ${className}`}
      style={{
        position: 'absolute',
        left: `${x}%`,
        top: `${y}%`,
        transform: `translate(-50%, -50%) rotate(${clampedRotation}deg) scale(${scale})`,
        opacity: 1,
        pointerEvents: 'none',
        userSelect: 'none',
        mixBlendMode: 'normal',
        filter: rareFilter,
        ...style
      }}
    >
      <img
        src={currentSrc}
        alt="rare decal"
        style={{
          display: 'block',
          width: typeof size === 'number' ? `${size}px` : size,
          height: 'auto',
          pointerEvents: 'none'
        }}
        onError={() => {
          if (!currentSrc.startsWith('/') && !currentSrc.startsWith('http')) {
            setCurrentSrc(`/${currentSrc}`);
          }
        }}
      />
    </div>
  );
};

export default RareItem;
